import { useQuery } from "@tanstack/react-query";
import { interpolateYlOrRd } from "d3-scale-chromatic";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect, useMemo, useState } from "react";
import { GeoJSON, MapContainer, TileLayer } from "react-leaflet";

import { getMapaCalor, type Mercado } from "@/lib/api";

// El bundler de Leaflet apunta a íconos vía require() que no aplica en Vite.
// No usamos markers en este mapa, pero forzamos la deshabilitación por si
// alguien añade uno sin saber. Sin esto, los markers default explotan.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;

type EstadoProps = { state_code: number; state_name: string };

type ZonaMap = {
  _meta?: unknown;
  keywords: Record<string, number>;
};

const MERCADOS: Mercado[] = ["MDA", "MTR"];
const STORAGE_KEY = "portal-edocta:mapa-calor-config";

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function currentMonthRange(): { desde: string; hasta: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { desde: isoLocal(first), hasta: isoLocal(last) };
}

function normalizeKey(s: string): string {
  return s
    .toUpperCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const PRECIO_FMT = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmtPrecio(v: string | number | null): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  return PRECIO_FMT.format(n);
}

type ZonaItem = { zona_carga: string; sistema: string; precio: number };

type EstadoAgg = {
  state_code: number;
  sistema: string;
  precio_promedio: number;
  zonas: ZonaItem[];
};

type Escala = { min: number; max: number };

export function MapaCalorPndPage() {
  const defaultRange = useMemo(currentMonthRange, []);
  const stored = useMemo<{
    mercado?: Mercado;
    desde?: string;
    hasta?: string;
    hora?: number | null;
  }>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [mercado, setMercado] = useState<Mercado>(stored.mercado ?? "MDA");
  const [desde, setDesde] = useState<string>(stored.desde ?? defaultRange.desde);
  const [hasta, setHasta] = useState<string>(stored.hasta ?? defaultRange.hasta);
  const [hora, setHora] = useState<number | null>(stored.hora ?? null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mercado, desde, hasta, hora }));
    } catch {
      // ignore
    }
  }, [mercado, desde, hasta, hora]);

  const estadosQ = useQuery({
    queryKey: ["mapa-calor:geojson"],
    queryFn: () =>
      fetch("/geo/mexico-estados.geojson").then((r) => {
        if (!r.ok) throw new Error(`GeoJSON ${r.status}`);
        return r.json() as Promise<FeatureCollection<Geometry, EstadoProps>>;
      }),
    staleTime: Infinity,
  });

  const zonaMapQ = useQuery({
    queryKey: ["mapa-calor:zonas-mapping"],
    queryFn: () =>
      fetch("/geo/zonas_estados.json").then((r) => {
        if (!r.ok) throw new Error(`mapping ${r.status}`);
        return r.json() as Promise<ZonaMap>;
      }),
    staleTime: Infinity,
  });

  const dataQ = useQuery({
    queryKey: ["mapa-calor", mercado, desde, hasta, hora],
    queryFn: () => getMapaCalor({ mercado, desde, hasta, hora }),
  });

  const { porEstado, sinAsignar, escalas } = useMemo(() => {
    const empty = {
      porEstado: new Map<number, EstadoAgg>(),
      sinAsignar: [] as ZonaItem[],
      escalas: new Map<string, Escala>(),
    };
    if (!dataQ.data || !zonaMapQ.data) return empty;

    const kws = Object.entries(zonaMapQ.data.keywords)
      .map(([k, code]) => ({ key: normalizeKey(k), code }))
      .sort((a, b) => b.key.length - a.key.length); // matches más largos ganan

    const porEstado = new Map<number, EstadoAgg>();
    const sinAsignar: ZonaItem[] = [];

    for (const row of dataQ.data.items) {
      const precio = row.precio_promedio != null ? Number(row.precio_promedio) : NaN;
      if (!Number.isFinite(precio)) continue;
      const item: ZonaItem = {
        zona_carga: row.zona_carga,
        sistema: row.sistema,
        precio,
      };
      const norm = normalizeKey(row.zona_carga);
      const hit = kws.find((k) => norm.includes(k.key));
      if (!hit) {
        sinAsignar.push(item);
        continue;
      }
      const agg = porEstado.get(hit.code);
      if (agg) {
        agg.zonas.push(item);
      } else {
        porEstado.set(hit.code, {
          state_code: hit.code,
          sistema: row.sistema,
          precio_promedio: 0,
          zonas: [item],
        });
      }
    }

    // Sistema mayoritario por estado (usualmente único — geografía exclusiva).
    porEstado.forEach((agg) => {
      const conteo = new Map<string, number>();
      agg.zonas.forEach((z) => conteo.set(z.sistema, (conteo.get(z.sistema) ?? 0) + 1));
      agg.sistema = [...conteo.entries()].sort((a, b) => b[1] - a[1])[0][0];
      const sum = agg.zonas.reduce((s, z) => s + z.precio, 0);
      agg.precio_promedio = sum / agg.zonas.length;
    });

    // Escala independiente por sistema.
    const escalas = new Map<string, Escala>();
    porEstado.forEach((agg) => {
      const cur = escalas.get(agg.sistema);
      if (!cur) {
        escalas.set(agg.sistema, { min: agg.precio_promedio, max: agg.precio_promedio });
      } else {
        if (agg.precio_promedio < cur.min) cur.min = agg.precio_promedio;
        if (agg.precio_promedio > cur.max) cur.max = agg.precio_promedio;
      }
    });

    return { porEstado, sinAsignar, escalas };
  }, [dataQ.data, zonaMapQ.data]);

  const styleFn = useMemo(() => {
    return (feature?: Feature<Geometry, EstadoProps>) => {
      const sinDatos = {
        fillColor: "#e2e8f0",
        color: "#94a3b8",
        weight: 1,
        fillOpacity: 0.4,
      };
      if (!feature) return sinDatos;
      const agg = porEstado.get(feature.properties.state_code);
      if (!agg) return sinDatos;
      const esc = escalas.get(agg.sistema);
      if (!esc) return sinDatos;
      const span = esc.max - esc.min;
      const t = span > 0 ? (agg.precio_promedio - esc.min) / span : 0.5;
      return {
        fillColor: interpolateYlOrRd(t),
        color: "#475569",
        weight: 1,
        fillOpacity: 0.85,
      };
    };
  }, [porEstado, escalas]);

  const onEachFeature = useMemo(() => {
    return (feature: Feature<Geometry, EstadoProps>, layer: L.Layer) => {
      const code = feature.properties.state_code;
      const name = feature.properties.state_name;
      const agg = porEstado.get(code);
      const lines: string[] = [`<div class="font-medium">${escapeHtml(name)}</div>`];
      if (!agg) {
        lines.push('<div class="text-slate-500 text-xs">Sin datos</div>');
      } else {
        lines.push(
          `<div class="text-xs">Sistema: <b>${escapeHtml(agg.sistema)}</b></div>`,
        );
        lines.push(
          `<div class="text-xs">Promedio: <b>$${fmtPrecio(agg.precio_promedio)}</b></div>`,
        );
        lines.push(`<div class="text-xs">${agg.zonas.length} zona(s) PND</div>`);
        const top = [...agg.zonas]
          .sort((a, b) => b.precio - a.precio)
          .slice(0, 5)
          .map(
            (z) =>
              `<div class="text-[11px] font-mono">${escapeHtml(z.zona_carga)} — $${fmtPrecio(z.precio)}</div>`,
          )
          .join("");
        if (top) lines.push(`<div class="mt-1 pt-1 border-t">${top}</div>`);
      }
      layer.bindTooltip(lines.join(""), { sticky: true, direction: "auto" });
    };
  }, [porEstado]);

  const geojsonKey = useMemo(() => {
    const parts = [...escalas.entries()]
      .map(([s, e]) => `${s}:${e.min}:${e.max}`)
      .join("|");
    return `${parts}|${porEstado.size}`;
  }, [escalas, porEstado]);

  const isLoading = estadosQ.isLoading || dataQ.isLoading || zonaMapQ.isLoading;
  const error = estadosQ.error || dataQ.error || zonaMapQ.error;

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Field label="Mercado">
            <div className="inline-flex border rounded overflow-hidden w-full">
              {MERCADOS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMercado(m)}
                  className={`flex-1 px-3 py-1.5 text-sm ${
                    mercado === m
                      ? "bg-blue-600 text-white"
                      : "bg-white hover:bg-slate-50"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Desde">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </Field>
          <div className="md:col-span-2 flex items-end gap-2">
            <button
              type="button"
              onClick={() => {
                const r = currentMonthRange();
                setDesde(r.desde);
                setHasta(r.hasta);
              }}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Mes actual
            </button>
            {escalas.size > 0 && (
              <div className="flex-1 min-w-0 space-y-1">
                {[...escalas.entries()]
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([sistema, esc]) => (
                    <Leyenda
                      key={sistema}
                      sistema={sistema}
                      min={esc.min}
                      max={esc.max}
                    />
                  ))}
              </div>
            )}
          </div>
        </div>

        <HoraSlider hora={hora} onChange={setHora} />
        {dataQ.data && (
          <div className="text-xs text-slate-500 pt-2 border-t flex flex-wrap gap-x-4 gap-y-1">
            <span>
              {dataQ.data.items.length.toLocaleString("es-MX")} zonas PND
              ({porEstado.size} estados con datos, {sinAsignar.length} sin asignar)
            </span>
            <span>
              Hora: <b>{hora == null ? "Todas (promedio)" : `${pad(hora)}:00`}</b>
            </span>
            {dataQ.data.fecha_min_efectiva && dataQ.data.fecha_max_efectiva && (
              <span>
                Rango efectivo: {dataQ.data.fecha_min_efectiva} → {dataQ.data.fecha_max_efectiva}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        {isLoading && (
          <div className="p-8 text-center text-slate-500 text-sm">Cargando…</div>
        )}
        {error && !isLoading && (
          <div className="p-8 text-center text-red-600 text-sm">
            Error: {(error as Error).message}
          </div>
        )}
        {!isLoading && !error && estadosQ.data && (
          <div className="h-[640px]">
            <MapContainer
              center={[23.6, -102]}
              zoom={5}
              minZoom={4}
              maxZoom={9}
              scrollWheelZoom
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <GeoJSON
                key={geojsonKey}
                data={estadosQ.data}
                style={styleFn}
                onEachFeature={onEachFeature}
              />
            </MapContainer>
          </div>
        )}
      </div>

      {sinAsignar.length > 0 && (
        <details className="bg-white border rounded-lg p-4 text-sm">
          <summary className="cursor-pointer font-medium">
            Zonas sin estado asignado ({sinAsignar.length})
          </summary>
          <p className="text-xs text-slate-500 mt-2 mb-3">
            Estas zonas no matchearon ninguna palabra clave del mapping. Para
            incluirlas, agrega entradas en{" "}
            <code className="font-mono">frontend/public/geo/zonas_estados.json</code>.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
            {sinAsignar
              .sort((a, b) => b.precio - a.precio)
              .map((z) => (
                <div key={z.zona_carga} className="font-mono text-xs flex justify-between">
                  <span className="truncate">
                    [{z.sistema}] {z.zona_carga}
                  </span>
                  <span className="tabular-nums text-slate-600 ml-2">
                    ${fmtPrecio(z.precio)}
                  </span>
                </div>
              ))}
          </div>
        </details>
      )}
    </div>
  );
}

function HoraSlider({
  hora,
  onChange,
}: {
  hora: number | null;
  onChange: (h: number | null) => void;
}) {
  // 0 → "Todas", 1..24 → hora del día.
  const value = hora ?? 0;
  const labels = ["Todas", "3", "6", "9", "12", "15", "18", "21", "24"];
  const THUMB = 36; // px — debe coincidir con w-9 h-9 abajo
  const ratio = value / 24; // 0..1
  // Centro del thumb = thumb/2 + ratio*(width - thumb)
  // → left absoluto = ratio * 100% - ratio * thumb + 0  (porque el div ya ancla en su top-left)
  const indicatorLeft = `calc(${ratio * 100}% - ${ratio * THUMB}px)`;
  return (
    <div className="pt-2 border-t">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-slate-600">
          Hora del día
          <span className="ml-2 text-[10px] text-slate-400 font-normal">
            (0 = Todas, 1–24 = hora puntual)
          </span>
        </span>
        <span className="text-xs tabular-nums">
          {hora == null ? (
            <span className="font-semibold text-slate-700">Todas (promedio)</span>
          ) : (
            <span className="font-semibold text-slate-700">Hora {pad(hora)}:00</span>
          )}
        </span>
      </div>
      <div className="relative h-9 flex items-center">
        {/* Track gris */}
        <div className="absolute inset-x-0 h-2 rounded-full bg-slate-200 pointer-events-none" />
        {/* Input nativo: invisible pero captura drag/click. Thumb transparente del mismo tamaño que el círculo visible. */}
        <input
          type="range"
          min={0}
          max={24}
          step={1}
          list="hora-ticks"
          value={value}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange(v === 0 ? null : v);
          }}
          aria-label="Hora del día"
          className="absolute inset-0 w-full h-full bg-transparent appearance-none cursor-pointer
            [&::-webkit-slider-runnable-track]:bg-transparent
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:w-9
            [&::-webkit-slider-thumb]:h-9
            [&::-webkit-slider-thumb]:bg-transparent
            [&::-webkit-slider-thumb]:cursor-pointer
            [&::-moz-range-track]:bg-transparent
            [&::-moz-range-thumb]:w-9
            [&::-moz-range-thumb]:h-9
            [&::-moz-range-thumb]:bg-transparent
            [&::-moz-range-thumb]:border-0
            [&::-moz-range-thumb]:cursor-pointer"
        />
        <datalist id="hora-ticks">
          {Array.from({ length: 25 }, (_, i) => (
            <option key={i} value={i} label={i === 0 ? "Todas" : String(i)} />
          ))}
        </datalist>
        {/* Círculo visible con el número dentro */}
        <div
          className="absolute w-9 h-9 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center pointer-events-none shadow tabular-nums ring-2 ring-white"
          style={{ left: indicatorLeft }}
        >
          {value === 0 ? "T" : pad(value)}
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 tabular-nums mt-1 select-none">
        {labels.map((l, i) => (
          <span key={i} className={i === 0 ? "" : "w-3 text-center"}>
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Leyenda({
  sistema,
  min,
  max,
}: {
  sistema?: string;
  min: number;
  max: number;
}) {
  const stops = [0, 0.25, 0.5, 0.75, 1];
  const span = max - min;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        {sistema && (
          <span className="text-[10px] font-semibold text-slate-600 tracking-wide w-8">
            {sistema}
          </span>
        )}
        <div
          className="h-3 rounded flex-1"
          style={{
            background: `linear-gradient(to right, ${stops
              .map((t) => `${interpolateYlOrRd(t)} ${t * 100}%`)
              .join(", ")})`,
          }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-slate-500 tabular-nums mt-0.5 pl-10">
        <span>${fmtPrecio(min)}</span>
        <span>${fmtPrecio(min + span / 2)}</span>
        <span>${fmtPrecio(max)}</span>
      </div>
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
